import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AlertMessage from 'components/UI/V3/AlertMessage/AlertMessage';
import { MarketplaceStatus, saveOlbGaTrackingIdThunk, saveOlbGtmTrackingIdThunk } from '@spike/marketplace-action';
import Button from 'components/UI/V3/Button/Button';
import { Input } from 'components/UI/V3/Forms/Input/Input';
import { useApiClientWrapper } from 'hooks';
import { FunctionComponent, ReactNode, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import styles from './TrackingForm.module.scss';

interface TrackingFormProps {
    icon: string;
    title: string;
    fieldLabel: string;
    placeholder: string;
    infoText: ReactNode;
    currentValue: string;
    integration: 'ga' | 'gtm';
    onBack: () => void;
}

const TrackingForm: FunctionComponent<TrackingFormProps> = ({
    icon,
    title,
    fieldLabel,
    placeholder,
    infoText,
    currentValue,
    integration,
    onBack
}) => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();

    const [value, setValue] = useState(currentValue);
    const [touched, setTouched] = useState(false);

    const GA_REGEX = /^G-[A-Z0-9]{10}$/i;
    const GTM_REGEX = /^GTM-[A-Z0-9]{6,12}$/i;

    const trimmedValue = value.trim();

    const isValid =
        trimmedValue === '' || (integration === 'ga' ? GA_REGEX.test(trimmedValue) : GTM_REGEX.test(trimmedValue));
    const validationError =
        touched && !isValid
            ? `Invalid format. Expected: ${
                  integration === 'ga' ? 'G-XXXXXXXXXX (10 chars)' : 'GTM-XXXXXXX (7 or more characters)'
              }`
            : null;

    const status = useSelector((state: RootState) => state.marketplace.status);

    const savedValue = useSelector((state: RootState) =>
        integration === 'ga'
            ? state.marketplace.marketplace.integrations.googleAnalyticsTrackingId
            : state.marketplace.marketplace.integrations.gtmTrackingId
    );

    const isConnected = !!savedValue;

    const isSaving =
        status === MarketplaceStatus.SavingOlbGaTrackingId || status === MarketplaceStatus.SavingOlbGtmTrackingId;

    const handleSave = () => {
        if (integration === 'ga') {
            dispatch(saveOlbGaTrackingIdThunk(apiClientWrapper, trimmedValue));
        } else {
            dispatch(saveOlbGtmTrackingIdThunk(apiClientWrapper, trimmedValue));
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <FontAwesomeIcon
                    icon={faArrowLeft}
                    className={styles.backIcon}
                    onClick={onBack}
                />
                <img
                    src={icon}
                    alt={title}
                    className={styles.icon}
                />
                <h2 className={styles.title}>{title}</h2>
                <span className={isConnected ? styles.badgeConnected : styles.badgeDisconnected}>
                    {isConnected ? 'Connected' : 'Not Connected'}
                </span>
            </div>

            <div className={styles.formBody}>
                <Input
                    label={fieldLabel}
                    placeholder={placeholder}
                    value={value}
                    autoFocus={false}
                    upperCase
                    error={validationError}
                    onChange={e => setValue(e.target.value)}
                    onBlur={() => setTouched(true)}
                />

                <AlertMessage
                    icon={faInfoCircle}
                    iconColor="#BAA997"
                    bdColor="#BCB8AE"
                    bgColor="#F8F5F1"
                >
                    <span className={styles.infoText}>{infoText}</span>
                </AlertMessage>

                <Button
                    label={isSaving ? 'Saving...' : isConnected && trimmedValue === '' ? 'Disconnect' : 'Save'}
                    isLoading={isSaving}
                    disabled={isSaving || !isValid}
                    onClick={handleSave}
                    className={styles.saveButton}
                />
            </div>
        </div>
    );
};

export default TrackingForm;
